import { TTemplateCreationStatus } from "./merchant";

// ─── Selector types ────────────────────────────────────────────────────────

export type TTemplateMode = "new" | "template";
export type TTemplateView = "desktop" | "mobile" | "both";

/**
 * Which half(s) of the checkout flow an extension run exercised. Drives which
 * QA report blocks the panel shows — a "sku-only" run never touches the auto
 * (coupon) half, and an "auto-only" run never touches the SKU (basket-capture)
 * half. Persisted on the run so a half that was never applicable to this run
 * can be told apart from one that ran but wasn't rated by the reviewer.
 */
export type TRunMode = "both" | "auto-only" | "sku-only";

export interface IAutoCouponSelectorEntry {
  selector: string | null;
  validSelectors: string[];
}

export interface IAutoCouponSelectors {
  couponBoxSelector: IAutoCouponSelectorEntry | null;
  toggleCouponBoxSelector: IAutoCouponSelectorEntry | null;
  submitCodeButtonSelector: IAutoCouponSelectorEntry | null;
  finalPriceSelector: IAutoCouponSelectorEntry | null;
  failureAlertSelector: IAutoCouponSelectorEntry | null;
  removeCodeSelector: IAutoCouponSelectorEntry | null;
  loadingSelector?: IAutoCouponSelectorEntry | null;
  emptyCartSelector?: IAutoCouponSelectorEntry | null;
  confirmRemoveCodeSelector?: IAutoCouponSelectorEntry | null;
  filteredDomSelectors?: string[];
}

export interface IAutoCouponSKUSelectors {
  basketAmountSelector: IAutoCouponSelectorEntry | null;
  itemNameSelector: IAutoCouponSelectorEntry | null;
  itemQuantitySelector: IAutoCouponSelectorEntry | null;
  itemPriceSelector: IAutoCouponSelectorEntry | null;
  itemCategorySelector: IAutoCouponSelectorEntry | null;
  // Optional: the extension engine does not capture these; SQL TemplateData has them.
  itemOriginalPriceSelector?: IAutoCouponSelectorEntry | null;
  itemProductIdSelector?: IAutoCouponSelectorEntry | null;
  itemProductUrlSelector?: IAutoCouponSelectorEntry | null;
}

export interface IAutoCouponTimings {
  applyCodeTimeout: number;
  removeCodeTimeout: number;
  betweenActionTimeout: number;
  betweenCodesTimeout: number;
  /** Set when a test run saw the page reload on apply; bound to SQL HasSiteRefresh. */
  hasSiteRefresh?: boolean;
}

// ─── Template ──────────────────────────────────────────────────────────────

/**
 * Auto-coupon template version — an INTEGER on the SQL row. New live templates
 * default to this when a staging template has no explicit version (matches the
 * extension's TEMPLATE_VERSION). Bump when the auto engine format changes.
 */
export const DEFAULT_AUTO_COUPON_TEMPLATE_VERSION = 2;

/**
 * SKU template version — a STRING label on the SQL row (e.g. "V1"), versioned
 * INDEPENDENTLY of the auto half. New SKU rows default to this; it must not be
 * derived from the auto integer version.
 */
export const DEFAULT_SKU_TEMPLATE_VERSION = "V1";

export interface IAutoCouponTemplate {
  _id: string;
  domain: string;
  /** Legacy field — older records keyed templates per-country; new writes omit it. */
  country?: string | null;
  selectors: IAutoCouponSelectors;
  skuSelectors: IAutoCouponSKUSelectors | null;
  /** True when the domain has working SKU selectors on this doc or a sibling doc. */
  hasWorkingSku: boolean;
  /** Which view currently carries the working SKU selectors for this domain. */
  workingSkuTemplateView: TTemplateView | null;
  /**
   * Working SKU version — a string label ("V1") from the doc that currently
   * carries SKU selectors. Versioned independently of the auto integer version.
   */
  workingSkuVersion: string | null;
  invalid: boolean;
  templateView: TTemplateView;
  templateMode: TTemplateMode;
  timings: IAutoCouponTimings | null;
  templateBuiltOnUrl: string | null;
  /** Mongo userId of the last person who saved this template */
  lastUserId: string | null;
  lastRunAt: string | null;
  lastRunSelectors: IAutoCouponSelectors | null;
  lastRunTemplateMode: TTemplateMode | null;
  lastRunTemplateBuiltOnUrl: string | null;
  lastSuccessfulRun: string | null;
  /** Set when a working template has been pushed to the Kindred SQL DB */
  syncedToSqlAt: string | null;
  /** Outcome of the most recent sync attempt for the auto half. */
  lastSyncAutoOutcome: TSyncHalfOutcome | null;
  /** Outcome of the most recent sync attempt for the SKU half. */
  lastSyncSkuOutcome: TSyncHalfOutcome | null;
  /** Whether the most recent sync attempt succeeded. */
  lastSyncSuccess: boolean | null;
  /** Message from the most recent sync attempt. */
  lastSyncMessage: string | null;
  /** Auto (checkout-page) URL regex — where the coupon template runs. */
  checkoutPageUrlRegex?: string | null;
  /** SKU (cart-page) URL regex — independent of the auto checkout regex. */
  skuCheckoutPageUrlRegex?: string | null;
  version?: number | null;
  /**
   * Email of the user behind the MOST RECENT run/evaluation for this domain
   * (the later of the two is used). Surfaced as "Created by" on the templates
   * list so a template can always be attributed to a person even when several
   * people have worked on it. Null when no run/evaluation carries a userId.
   */
  recentUserEmail: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Set when a SQL diff scan has detected that the live SQL values diverge from
   * the current staging values. Cleared when the template is next pushed to SQL.
   */
  sqlMismatch: boolean | null;
}

export interface IAutoCouponTemplateListResponse {
  templates: IAutoCouponTemplate[];
  total: number;
  page: number;
  limit: number;
}

export interface IAutoCouponTemplatesFilter {
  domain?: string;
  templateView?: TTemplateView;
  invalid?: boolean;
  syncedToSql?: boolean;
  page?: number;
  limit?: number;
}

// ─── Coupon codes ──────────────────────────────────────────────────────────

export interface IAutoCouponCode {
  id: string;
  code: string;
  summary: string;
  priority: number;
  success: boolean;
  savings: number;
  errorMessage?: string | null;
  /**
   * True when `errorMessage` matches a known "valid code, but not applicable
   * to this basket" pattern (see `isConfirmedNotApplicableMessage`) rather
   * than a genuine invalid/expired code. Mirrors the same field already used
   * by the other coupon-testing pipeline (see task-content.tsx) so both
   * surfaces render the distinction the same way.
   */
  notApplicableToBasket?: boolean;
}

export interface IAutoCouponCapturedItem {
  name: string;
  quantity: number;
  price: number | null;
  category: string | null;
  productUrl: string | null;
}

// ─── Evaluation ────────────────────────────────────────────────────────────

/** How an evaluation was produced: a QA extension run, an agentic session replay,
 *  or an unattended headless template-capture run. */
export type TEvaluationOrigin = "extension" | "session" | "capture";

/**
 * Marks a capture whose cookie jar carries a SIGNED-IN session, recorded by a
 * tester using Kindred's test account for that merchant.
 *
 * Separate from `origin`, which says how the capture was produced — an
 * authenticated capture still comes from the extension. It is its own field
 * because it changes how the session must be treated: it is the only way to
 * reach a login-walled or bot-walled checkout, it expires on the merchant's
 * terms rather than ours, and the jar is a secret.
 */
export type TAuthenticatedSession = boolean;

export interface IAutoCouponEvaluation {
  _id: string;
  correlationId: string;
  domain: string;
  country: string;
  userId: string | null;
  /** Email of the user who produced this evaluation (resolved from userId). */
  userEmail: string | null;
  /** "session" when produced by the headless session-replay engine. */
  origin: TEvaluationOrigin;
  templateMode: TTemplateMode;
  templateKeyMobile: boolean;
  codes: IAutoCouponCode[];
  capturedItems: IAutoCouponCapturedItem[];
  /** Auto (coupon) selectors that ran. */
  template: IAutoCouponSelectors | null;
  /**
   * SKU (basket-capture) selectors that ran, captured independently of the auto
   * `template`. Null when the run exercised no SKU template. Pairs with
   * `skuTemplateStatus` so a reviewer's SKU verdict has the selectors behind it.
   */
  skuTemplate: IAutoCouponSKUSelectors | null;
  /** Which view the run executed under. */
  templateView: TTemplateView | null;
  /** Auto template version under test (integer, matches the SQL row). */
  version: number | null;
  /** Auto checkout-page URL regex the coupon template ran against. */
  checkoutPageUrlRegex: string | null;
  /** SKU cart-page URL regex the SKU template captured on. */
  skuCheckoutPageUrlRegex: string | null;
  currency: string | null;
  originalTotal: number | null;
  mobileOnly: boolean;
  aiCalled: boolean;
  browserTimestamp: number;
  /**
   * Extension build version (chrome.runtime.getManifest().version) that
   * produced this run. Null for session/capture runs, which go through the
   * headless engine's chrome shim rather than a real installed extension.
   */
  extensionVersion: string | null;
  /**
   * Pass/fail verdict for each half, reported independently so each carries
   * its own status and its own free-text failure reason. For
   * extension/session-origin evaluations this is a manual QA verdict
   * recorded from the extension panel. For capture-origin evaluations there
   * is no human reviewer in the loop, so this is set automatically by the
   * capture engine's own end-of-run functional check (see
   * evaluateCaptureAutoVerdict/evaluateCaptureSkuVerdict in
   * auto-coupons-engine.ts). Defaults to "not_tested"/null until either
   * writes a report.
   */
  autoTemplateStatus: TSessionRunPartStatus;
  skuTemplateStatus: TSessionRunPartStatus;
  autoFailureReason: string | null;
  skuFailureReason: string | null;
  /**
   * Reason the backend rejected the last auto-template save attempt for this
   * run (e.g. the AUTO_INCOMPLETE 422 thrown when couponBoxSelector /
   * submitCodeButtonSelector / checkoutPageUrlRegex aren't all present). Null
   * once a save succeeds. Distinct from `autoFailureReason`, which is the
   * reviewer's own manual verdict text, not a backend-thrown error.
   */
  templateSaveError: string | null;
  /** Same as `templateSaveError`, but for the last SKU-template save attempt. */
  skuTemplateSaveError: string | null;
  createdAt: string;
}

/**
 * Partial update of an evaluation's manual QA verdict, keyed by correlationId.
 * Upserts a minimal evaluation when none exists yet (e.g. a SKU-only run that
 * never produced a coupon evaluation). Only the provided verdict fields are
 * written; omitted fields are left untouched.
 */
export interface IAutoCouponEvaluationVerdictRequest {
  correlationId: string;
  domain: string;
  country?: string;
  templateMode?: TTemplateMode;
  autoTemplateStatus?: TSessionRunPartStatus;
  skuTemplateStatus?: TSessionRunPartStatus;
  autoFailureReason?: string | null;
  skuFailureReason?: string | null;
}

export interface IAutoCouponEvaluationListResponse {
  evaluations: IAutoCouponEvaluation[];
  total: number;
  page: number;
  limit: number;
}

// ─── Run / Log ─────────────────────────────────────────────────────────────

export type TAutoCouponLogLevel =
  | "warn"
  | "info"
  | "error"
  | "verbose"
  | "debug";
export type TAutoCouponLogTag =
  | "auto_coupons"
  | "coupon_evaluation"
  | "sku_template"
  | "ai_progress";

export interface IAutoCouponLogEntry {
  text: string;
  level: TAutoCouponLogLevel;
  tag?: TAutoCouponLogTag;
  timestamp: number;
}

export interface IAutoCouponRun {
  _id: string;
  correlationId: string;
  domain: string;
  userId: string | null;
  /** Email of the user who produced this run (resolved from userId). */
  userEmail: string | null;
  /** "session" when produced by the headless session-replay engine. */
  origin: TEvaluationOrigin;
  templateMode: TTemplateMode | null;
  /** Which view the run executed under (now persisted on the run itself). */
  templateView: TTemplateView | null;
  /** Country the coupon list was sourced from. */
  country: string | null;
  /**
   * Which half(s) this run exercised. Null for runs recorded before this was
   * tracked, and for non-extension origins where the concept doesn't apply.
   * Lets a reviewed run tell "not applicable" (this half was never run) apart
   * from "not tested" (it ran but wasn't rated) in `reviewerVerdict`.
   */
  runMode: TRunMode | null;
  /**
   * Extension build (chrome.runtime.getManifest().version) that produced
   * this run — sent alongside its completed evaluation, so it's null for a
   * run that fails before then, and for "session"/"capture" origins (the
   * headless engine, not the extension).
   */
  extensionVersion: string | null;
  logs: IAutoCouponLogEntry[];
  evaluationCompleted: boolean;
  /** True when either the automated run itself died mid-flight (tab backgrounded/navigated/closed before it finished — it does not always complete on its own), or it finished but the reviewer's tab/panel went away before a Pass/Fail verdict was submitted. */
  abandoned: boolean;
  /** Human-readable reason, e.g. "tab lost focus or was backgrounded mid-run — switched to example.com", or "...before the auto template verdict was submitted". Null unless `abandoned`. */
  abandonedReason: string | null;
  couponsEvaluated: number;
  successfulCoupons: number;
  unsuccessfulCoupons: number;
  bestSavings: number;
  currency: string | null;
  originalTotal: number | null;
  /**
   * Lightweight summary of the captured browser session. The heavy blobs
   * (cookies, localStorage, sessionStorage, sessionMetadata) are NOT included
   * in list responses — fetch a single run to replay the session.
   *
   * For "session"/"capture"-origin runs, `sessionCapturedAt` is inherited
   * from the extension run whose session was replayed rather than captured
   * by this run itself.
   */
  checkoutUrl: string | null;
  hasSession: boolean;
  sessionCapturedAt: string | null;
  /** True when this run is the extension's standalone "Capture session
   *  only" action — no AI stages ran, no coupon codes were attempted. */
  sessionCaptureOnly: boolean;
  /**
   * Total LLM cost for this specific run, joined in from its
   * TemplateCaptureRun by correlationId. Only populated for "capture"-origin
   * runs today — the interactive extension flow doesn't yet record cost
   * per-run (only as a merchant-level running total, see Merchant.aiSpend).
   * Null when there's no per-run cost record to join.
   */
  aiCost: number | null;
  /**
   * Manual QA verdict from the run's linked evaluation (matched by
   * correlationId), when a human has reviewed it. Null when no verdict has
   * been recorded — distinct from a verdict where every half is
   * "not_tested", which is surfaced as an actual reviewerVerdict so a
   * reviewed-but-not-yet-rated run doesn't look identical to an unreviewed
   * one. This is independent of `evaluationCompleted`, which only reflects
   * whether the automated coupon-testing loop finished — a run can be fully
   * reviewed (pass/fail on either half) even when that loop never ran (e.g.
   * a SKU-only run, or a run that hard-failed before reaching it).
   */
  reviewerVerdict: ISessionRunReviewerVerdict | null;
  createdAt: string;
  updatedAt: string;
}

export interface IAutoCouponRunListResponse {
  runs: IAutoCouponRun[];
  total: number;
  page: number;
  limit: number;
}

// ─── SQL sync ──────────────────────────────────────────────────────────────

export interface IAutoCouponSyncSqlRequest {
  templateId: string;
}

export interface IAutoCouponSyncSqlResponse {
  success: boolean;
  message: string;
  /** Outcome of the auto-coupon half (only set by the selective/bulk sync). */
  autoOutcome?: TSyncHalfOutcome;
  /** Outcome of the SKU half (only set by the selective/bulk sync). */
  skuOutcome?: TSyncHalfOutcome;
}

// ─── Careful SQL sync: field diff + selective push ─────────────────────────

/** A single comparable field between the working (Mongo) template and the live (SQL) one. */
export interface IAutoCouponTemplateFieldDiff {
  /** Stable identifier used as the key in a selective-sync selections map. */
  key: string;
  /** Human-friendly label shown in the push-live modal. */
  label: string;
  /** Grouping bucket for the UI (e.g. "selectors", "timings", "config"). */
  group: string;
  /** Value currently held in the working MongoDB template. */
  mongoValue: unknown;
  /** Value currently held in the live SQL template (null when not yet in SQL). */
  sqlValue: unknown;
  /** True when the two values differ. */
  differs: boolean;
}

export interface IAutoCouponTemplateCompareResponse {
  /** Whether a live template already exists in SQL for this domain. */
  existsInSql: boolean;
  domain: string;
  templateView: TTemplateView;
  fields: IAutoCouponTemplateFieldDiff[];
  /** The working (Mongo) template's own version — the one the engine built. */
  mongoVersion: number | null;
  /** Every existing SQL version row for this domain (for the version picker). */
  availableVersions: IAutoCouponSqlVersion[];
  /** The SQL version row the diff was computed against (null when none / pushing new). */
  comparedTemplateId: string | null;
  comparedVersion: number | null;
  /**
   * SKU-template (partners DB) half of the comparison. A live template is the
   * combination of an auto-coupon template (above) and a SKU template (here).
   */
  sku: IAutoCouponSkuCompare;
}

/** A single live SQL version row for a domain (used in the version picker). */
export interface IAutoCouponSqlVersion {
  templateId: string;
  version: number | null;
  isActive: boolean;
  priority: number;
}

/** A single SKU template version row for a domain (used in the SKU version picker). */
export interface IAutoCouponSkuVersion {
  templateId: string;
  /** SKU versions are string labels (e.g. "V1"), not numbers. */
  version: string | null;
  isActive: boolean;
}

/**
 * SKU-template (partners DB) half of the push-live comparison.
 */
export interface IAutoCouponSkuCompare {
  /** Whether the domain has any SKU template row in SQL. */
  existsInSql: boolean;
  /** Whether working SKU selectors (in Mongo) are available to push. */
  hasWorking: boolean;
  /** Per-field diff of the SKU selector fields (working vs live). */
  fields: IAutoCouponTemplateFieldDiff[];
  /** Every existing SKU version row for this domain (for the version picker). */
  availableVersions: IAutoCouponSkuVersion[];
  /** The SKU row the diff was computed against (null when none). */
  comparedTemplateId: string | null;
  comparedVersion: string | null;
}

/** Per-field choice: take the working (Mongo) value, or keep the live (SQL) value. */
export type TSyncFieldChoice = "take" | "keep";

/** Whether a selective sync updates an existing SQL version or inserts a new one. */
export type TSyncPushMode = "existing" | "new" | "skip";

/** Normalized per-half sync outcome used by audit and staging list UI. */
export type TSyncHalfOutcome =
  | "created"
  | "updated"
  | "failed"
  | "skipped"
  | "none";

/** Whether the SKU half updates an existing SKU row, inserts a new one, or is skipped. */
export type TSyncSkuPushMode = "existing" | "new" | "skip";

export interface IAutoCouponSyncSelectiveRequest {
  /** Map of field key → choice. Missing keys default to "keep". */
  selections: Record<string, TSyncFieldChoice>;
  /** Update an existing SQL version row, or insert a brand-new version. */
  mode: TSyncPushMode;
  /** When mode === "existing": the SQL templateId (version row) to update. */
  targetTemplateId?: string | null;
  /** When mode === "new": the version number to assign (defaults to the Mongo version). */
  newVersion?: number | null;
  /** SKU push mode: update an existing SKU row, insert a new one, or skip the SKU half. */
  skuMode?: TSyncSkuPushMode;
  /** When skuMode === "existing": the SKU templateId (row) to update. */
  skuTargetTemplateId?: string | null;
  /** When skuMode === "new": the version label to assign (e.g. "V2"). */
  skuNewVersion?: string | null;
}

/** The resolved outcome for a single field after a selective sync. */
export interface IAutoCouponSyncFieldChange {
  key: string;
  label: string;
  choice: TSyncFieldChoice;
  mongoValue: unknown;
  sqlValue: unknown;
  /** The value actually written to SQL for this field. */
  appliedValue: unknown;
}

export interface IAutoCouponSyncAudit {
  _id: string;
  domain: string;
  templateView: TTemplateView;
  /** Mongo template _id that was synced. */
  templateId: string;
  /**
   * A live template is a combination of two SQL templates. These capture both
   * component rows affected/referenced by the sync:
   */
  /** AutoCouponsTemplates row id (campaigns DB) written by this sync. */
  autoTemplateId: string | null;
  /** SKUTemplates row id (partners DB) associated with the domain. */
  skuTemplateId: string | null;
  /** Mongo userId of the person who performed the sync. */
  userId: string | null;
  /** Email of the person who performed the sync (for human-readable audit). */
  userEmail: string | null;
  syncedAt: string;
  success: boolean;
  autoOutcome: TSyncHalfOutcome;
  skuOutcome: TSyncHalfOutcome;
  message: string;
  changes: IAutoCouponSyncFieldChange[];
}

export interface IAutoCouponSyncAuditListResponse {
  audits: IAutoCouponSyncAudit[];
  total: number;
  page: number;
  limit: number;
}

// ─── Bulk "put live" for items missing a live template ──────────────────────

/** Hard cap on how many items one bulk put-live request will process. */
export const AUTO_COUPON_BULK_PUT_LIVE_MAX = 250;

/**
 * Which items a bulk put-live run includes. All modes only process valid
 * staging templates — invalid-flagged docs are never pushed.
 *
 * - "both-absent" (default): push only when BOTH the auto template AND the SKU
 *   template are absent from SQL (no existing live row for either half) AND
 *   both are valid in staging. Domains where even one half already has a live
 *   SQL row are skipped entirely — those go through the per-row push-live flow.
 *
 * - "one-absent": push whichever half does not yet have a live SQL row,
 *   independently of the other half. Auto is pushed if no live auto row exists;
 *   SKU is pushed if no live SKU row exists. Halves that already have a live
 *   row in SQL are left completely untouched.
 *
 * - "all-updates": push all outstanding work — halves that are new to SQL AND
 *   halves that already have a live SQL row but whose staging values have
 *   diverged since the last sync (SQL-diff items). This is the most aggressive
 *   mode.
 */
export type TAutoCouponPutLiveMode =
  | "both-absent"
  | "one-absent"
  | "all-updates";

/**
 * A staging template that still needs to be put live (or updated), collapsed
 * to one row per domain (mobile view preferred when a domain has multiple).
 * `needsAuto` / `needsSku` flag which half has no active row in SQL yet.
 * `pushAuto` / `pushSku` flag which half will actually be acted on by the bulk
 * run — in "updates" mode these can be true even when the SQL row already
 * exists (meaning the existing row will be updated with current staging values).
 */
export interface IAutoCouponUnsyncedTemplate extends IAutoCouponTemplate {
  /** No active auto-coupon template row exists in SQL for this domain. */
  needsAuto: boolean;
  /** Working SKU selectors exist but no active SKU row exists in SQL. */
  needsSku: boolean;
  /**
   * True when the auto half will be acted on by this bulk run — either
   * creating a new row (needsAuto=true) or updating an existing one
   * (needsAuto=false, "updates" mode only).
   */
  pushAuto: boolean;
  /**
   * True when the SKU half will be acted on by this bulk run — either
   * creating a new row (needsSku=true) or updating an existing one
   * (needsSku=false, "updates" mode only).
   */
  pushSku: boolean;
  /**
   * True when the domain has BOTH a valid auto and a valid SKU template on the
   * dashboard (working staging selectors for each half). The "both-absent" mode
   * requires this flag AND that both halves are absent from SQL. False for
   * "auto-only" domains (no valid dashboard SKU), which the default mode skips.
   */
  bothValid: boolean;
}

export interface IAutoCouponUnsyncedListResponse {
  templates: IAutoCouponUnsyncedTemplate[];
  total: number;
  page: number;
  limit: number;
}

/** Per-domain outcome of the bulk put-live run — one row of the CSV export. */
export interface IAutoCouponBulkPutLiveResultItem {
  domain: string;
  templateView: TTemplateView;
  /** The auto-coupon template was put live by this run. */
  autoLive: boolean;
  /** The SKU template was put live by this run. */
  skuLive: boolean;
  success: boolean;
  message: string;
}

export interface IAutoCouponBulkPutLiveResponse {
  results: IAutoCouponBulkPutLiveResultItem[];
  /** How many items were processed. */
  totalProcessed: number;
  /** Count where the auto-coupon template was put live. */
  autoLiveCount: number;
  /** Count where the SKU template was put live. */
  skuLiveCount: number;
}

/** Result of the SQL diff scan triggered by the "Check SQL diff" button. */
export interface IAutoCouponSqlDiffCheckResponse {
  /** Total number of staging templates compared. */
  checked: number;
  /** Number of domains where staging values differ from SQL. */
  mismatches: number;
  /** Domains where a mismatch was detected. */
  mismatchedDomains: string[];
}

// ─── Live-template tool testing ────────────────────────────────────────────

export type TAutoCouponToolTestStatus = "pass" | "fail";

/** One recorded run of the auto-coupon tool against a live (SQL) template. */
export interface IAutoCouponToolTestEntry {
  testedAt: string;
  status: TAutoCouponToolTestStatus;
  correlationId: string | null;
  evaluationCompleted: boolean;
  successfulCoupons: number;
  couponsEvaluated: number;
  bestSavings: number;
  currency: string | null;
  userId: string | null;
}

/** Tool-test history for a (domain, templateView, country) combination. */
export interface IAutoCouponToolTest {
  _id: string;
  domain: string;
  templateView: TTemplateView;
  country: string;
  lastToolTestedAt: string | null;
  lastStatus: TAutoCouponToolTestStatus | null;
  history: IAutoCouponToolTestEntry[];
}

/**
 * Raw auto-coupon selector columns straight from the AutoCouponsTemplates SQL
 * row — plain strings, no `validSelectors` wrapping. Reflects the live row.
 */
export interface IAutoCouponLiveSelectors {
  couponBoxSelector: string | null;
  toggleCouponBoxSelector: string | null;
  extraToggleCouponBoxSelector: string | null;
  submitCodeButtonSelector: string | null;
  finalPriceSelector: string | null;
  failureAlertSelector: string | null;
  successAlertSelector: string | null;
  removeCodeSelector: string | null;
  confirmRemoveCodeSelector: string | null;
  emptyCartSelector: string | null;
  loadingSelector: string | null;
}

/**
 * Raw auto-coupon timing / behaviour columns straight from the
 * AutoCouponsTemplates SQL row.
 */
export interface IAutoCouponLiveTimings {
  applyCodeTimeout: number | null;
  removeCodeTimeout: number | null;
  betweenActionTimeout: number | null;
  betweenCodesTimeout: number | null;
  failureCheckFrequency: number | null;
  couponBoxCheckFrequency: number | null;
  hasSiteRefresh: boolean;
  manualRefresh: boolean;
}

/** A live template read directly from Azure SQL, enriched with tool-test status. */
export interface IAutoCouponLiveTemplate {
  templateId: string;
  domain: string;
  /** SQL only distinguishes mobile-only vs both. */
  templateView: "mobile" | "both";
  isActive: boolean;
  priority: number;
  version: number | null;
  /**
   * Raw auto-coupon selector columns straight from SQL — no `validSelectors`
   * wrapping, no defaults. Reflects the live row exactly.
   */
  selectors: IAutoCouponLiveSelectors;
  /** Raw auto-coupon timing / behaviour columns straight from SQL. */
  timings: IAutoCouponLiveTimings;
  checkoutPageUrlRegex: string | null;
  /**
   * A live template combines the auto-coupon template (above) with a SKU
   * template stored in the partners DB. These describe the SKU half:
   */
  /** SKUTemplates row id (partners DB), null when the domain has no SKU template. */
  skuTemplateId: string | null;
  /** Raw SKU template version label (e.g. "V1"), null when absent. */
  skuVersion: string | null;
  /** Whether the SKU template is active. */
  skuIsActive: boolean | null;
  /**
   * Full parsed SKU TemplateData JSON exactly as stored (selector keys plus any
   * extra fields such as `checkoutUrl`). Null when the domain has no SKU template
   * or the stored JSON could not be parsed.
   */
  skuTemplateData: Record<string, unknown> | null;
  /** SKU row CreatedAt, null when absent. */
  skuCreatedAt: string | null;
  /** SKU row UpdatedAt, null when absent. */
  skuUpdatedAt: string | null;
  /** Most-recent tool test across all countries for this template. */
  lastToolTestedAt: string | null;
  lastStatus: TAutoCouponToolTestStatus | null;
  /** Per-country tool-test breakdown (expandable in the UI). */
  toolTests: IAutoCouponToolTest[];
}

export interface IAutoCouponLiveTemplateListResponse {
  templates: IAutoCouponLiveTemplate[];
  total: number;
  page: number;
  limit: number;
}

// ─── Template stats (cards on the templates page) ────────────────────────────

/**
 * One stat split into auto-coupon / SKU / combined figures. Every figure is a
 * count of unique domains (www-stripped, lowercased); `combined` is the size
 * of the union of the auto and SKU domain sets, not their sum.
 */
export interface IAutoCouponTemplateStatsFigures {
  auto: number;
  sku: number;
  combined: number;
  /** Domains that have BOTH an active auto template AND an active SKU template. */
  both: number;
}

// ─── Session runs (one document per agentic session-replay run) ─────────────

/**
 * What failed, if anything:
 * - "pass": at least one coupon code applied successfully
 * - "coupons_failed": template worked end-to-end but no code gave a discount
 * - "template_failed": engine ran but the template broke before/while testing
 * - "session_failed": the captured session couldn't be restored at all
 * - "no_coupons": merchant has no approved coupon codes to test
 * - "no_template": no live SQL template exists for this domain
 */
export type TSessionRunStatus =
  | "pass"
  | "coupons_failed"
  | "template_failed"
  | "session_failed"
  | "no_coupons"
  | "no_template";

/**
 * Outcome of one independently-testable part of a session run:
 * - "pass" / "fail": the part was exercised and worked / broke
 * - "not_tested": the run never reached it (session failed, or the domain
 *   has no live SKU template — AI regeneration is disabled on monitoring runs)
 */
export type TSessionRunPartStatus = "pass" | "fail" | "not_tested";

/**
 * Lifecycle state of a session run, independent of its outcome (`status`):
 * - "scheduled": a task was created for this run but a runner hasn't started it
 * - "running": a runner picked it up and the run is in progress
 * - "completed": the run finished gracefully (see `status` for the verdict)
 * - "failed": the run threw before it could complete (never reached
 *   TASK_COMPLETE — e.g. browser crash, timeout, or a pod restart mid-run)
 */
export type TSessionRunState = "scheduled" | "running" | "completed" | "failed";

/**
 * Manual QA verdict carried on a run's linked evaluation (an extension
 * reviewer's independent auto/SKU pass·fail + free-text reasons). Surfaced on
 * session runs alongside the engine's own automatic verdict.
 */
export interface ISessionRunReviewerVerdict {
  autoTemplateStatus: TSessionRunPartStatus;
  skuTemplateStatus: TSessionRunPartStatus;
  autoFailureReason: string | null;
  skuFailureReason: string | null;
}

export interface ISessionRunResult {
  _id: string;
  /** This session run's correlationId (sess-<taskId>-<ts>). */
  correlationId: string;
  /** Extension run whose captured session was replayed. */
  sourceRunId: string | null;
  taskId: string;
  merchantId: string | null;
  domain: string;
  country: string | null;
  /** Lifecycle state (scheduled → running → completed/failed). */
  runState: TSessionRunState;
  /** Overall verdict (first thing that broke). Null until the run finishes. */
  status: TSessionRunStatus | null;
  /** Did the live SKU template capture the basket? */
  skuTemplateStatus: TSessionRunPartStatus;
  /** Did the live coupon template drive the checkout end-to-end? */
  autoTemplateStatus: TSessionRunPartStatus;
  /** Did at least one coupon code actually apply? */
  couponsStatus: TSessionRunPartStatus;
  failureReason: string | null;
  /** Per-half failure reasons derived by the engine (auto vs SKU). */
  autoFailureReason: string | null;
  skuFailureReason: string | null;
  errorMessage: string | null;
  // Template under test
  autoTemplateId: string | null;
  autoTemplateVersion: number | null;
  autoTemplateSource: "mongo" | "sql" | null;
  templateView: TTemplateView | null;
  skuSource: "mongo" | "sql" | null;
  aiCalled: boolean;
  // Coupon outcomes
  codes: IAutoCouponCode[];
  couponsEvaluated: number;
  successfulCoupons: number;
  bestSavings: number;
  currency: string | null;
  originalTotal: number | null;
  // SKU capture (judge the SKU template)
  capturedItems: IAutoCouponCapturedItem[];
  // Links to the connected records
  evaluationId: string | null;
  testResultId: string | null;
  // Replay context
  checkoutUrl: string | null;
  sessionAgeMs: number | null;
  /** When the run was enqueued (task created), before pickup. */
  scheduledAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  /**
   * Manual QA verdict from the linked evaluation, when a human has reviewed
   * this run. Null when the evaluation has no manual verdict recorded.
   */
  reviewerVerdict: ISessionRunReviewerVerdict | null;
  createdAt: string;
}

export interface ISessionRunListResponse {
  sessionRuns: ISessionRunResult[];
  total: number;
  page: number;
  limit: number;
}

// ─── Live status summary (top of the Session Runs page) ─────────────────────

/** A single in-flight run (scheduled or running) for the live summary. */
export interface ISessionRunLiveItem {
  _id: string;
  taskId: string;
  merchantId: string | null;
  domain: string;
  country: string | null;
  /** scheduledAt for pending runs, startedAt for running runs. */
  since: string | null;
  /** Milliseconds since `since` (how long it's been waiting/running). */
  ageMs: number | null;
}

/**
 * A point-in-time view of the session runner: what's queued, what's actively
 * running, and — crucially — how many merchants are due right now but haven't
 * been picked up (a persistent gap here means no runner is claiming them).
 */
export interface ISessionRunStatusSummary {
  /** Runs whose task exists but hasn't started (runState "scheduled"). */
  pending: number;
  /** Runs a runner is actively executing (runState "running"). */
  running: number;
  /** Terminal runs recorded since local midnight. */
  completedToday: number;
  failedToday: number;
  /** Merchant-side schedule view (source of the "not picked up" signal). */
  merchants: {
    /** Session merchants with scheduling enabled. */
    scheduledTotal: number;
    /** Enabled session merchants whose nextScheduleRun is due (<= now). */
    dueNow: number;
    /** Session merchants currently holding a processing lock. */
    locked: number;
  };
  pendingRuns: ISessionRunLiveItem[];
  runningRuns: ISessionRunLiveItem[];
}

// ─── Session-run daily reporting (Reporting tab on the Session Runs page) ────

/** Longest window the Session Runs reporting tab will chart / tabulate. */
export const SESSION_RUN_REPORT_MAX_DAYS = 30;

/**
 * Per-status counts for one UTC day, aggregated over every session run whose
 * verdict (`status`) is set — i.e. runs that completed with an outcome. Runs
 * still scheduled/running, or that hard-failed before recording a verdict, are
 * excluded (they have no `status`). Every day in the requested window is
 * present, zero-filled, so the trend chart is continuous.
 */
export interface ISessionRunDailyReportEntry {
  /** UTC calendar day, YYYY-MM-DD. */
  date: string;
  /** Runs with a verdict recorded that day (sum of the status buckets below). */
  total: number;
  pass: number;
  couponsFailed: number;
  templateFailed: number;
  sessionFailed: number;
  noCoupons: number;
  noTemplate: number;
  /** pass / total, in the range 0–1 (0 when total is 0). */
  passRate: number;
}

export interface ISessionRunDailyReportResponse {
  /** Ascending by date; one entry per day in the window (zero-filled). */
  entries: ISessionRunDailyReportEntry[];
  /** Number of days the window covers. */
  days: number;
  /** The most recent day in the window (UTC today), for the "today" cards. */
  latestDate: string | null;
}

/**
 * One row of the per-(day, domain, geo) breakdown used by the domain/geo CSV
 * export. Same status buckets as the daily entry, keyed additionally by the
 * domain and country. Only combinations that actually ran appear (not
 * zero-filled).
 */
export interface ISessionRunDomainGeoReportEntry {
  date: string;
  domain: string;
  country: string | null;
  total: number;
  pass: number;
  couponsFailed: number;
  templateFailed: number;
  sessionFailed: number;
  noCoupons: number;
  noTemplate: number;
  passRate: number;
}

// ─── Session stats (Session Merchants page cards) ───────────────────────────

export interface IAutoCouponSessionStatsFailureReason {
  /** Task FailureReasonType value (e.g. "session_expired"), or "unknown". */
  reason: string;
  count: number;
}

export interface IAutoCouponSessionStats {
  /** Merchants with a captured, replayable session. */
  sessionMerchants: {
    total: number;
    /** Subset currently executing scheduled runs as session replays. */
    inSessionMode: number;
    /** Distinct domains whose session was (re)captured in the last 24 hours. */
    capturedLast24h: number;
  };
  /** Staging (Mongo) templates created in the last 24 hours. */
  templatesCreated: {
    last24h: number;
    /** Subset not flagged invalid. */
    valid: number;
  };
  /** Extension (QA user) evaluations in the last 24 hours. */
  testedByUsers: {
    evaluations: number;
    /** Evaluations where at least one code applied successfully. */
    passed: number;
    /** Distinct domains covered. */
    domains: number;
  };
  /** Agentic session runs (runType "session" tasks) in the last 24 hours. */
  agenticRuns: {
    total: number;
    working: number;
    failed: number;
    failureReasons: IAutoCouponSessionStatsFailureReason[];
  };
  /** When these figures were computed — the server caches them briefly. */
  cachedAt: string;
}

export interface IAutoCouponTemplateStats {
  /** Domains with an active row in SQL (AutoCouponsTemplates / SKUTemplates). */
  live: IAutoCouponTemplateStatsFigures;
  /** Domains successfully pushed live through this tool (sync-audit outcome created/updated). */
  synced: IAutoCouponTemplateStatsFigures;
  /** Valid staging domains never successfully pushed live via this tool. */
  outstanding: IAutoCouponTemplateStatsFigures;
  /**
   * Subset of outstanding: valid staging where the domain has NO live SQL row
   * yet for that half. These are brand-new — safe to push with "both-absent" or
   * "one-absent" mode.
   */
  outstandingNew: IAutoCouponTemplateStatsFigures;
  /**
   * Subset of outstanding: valid staging where the domain already has a live
   * SQL row but staging values have diverged since the last sync (SQL diff).
   * These require "all-updates" mode or a manual per-row push.
   */
  outstandingUpdates: IAutoCouponTemplateStatsFigures;
  /** Staging domains whose template document was created in the last 24 hours. */
  createdLast24h: IAutoCouponTemplateStatsFigures;
  /** Subset of createdLast24h that is valid (not flagged invalid). */
  createdLast24hValid: IAutoCouponTemplateStatsFigures;
  /** When these figures were computed — the server caches them briefly. */
  cachedAt: string;
}

// ─── AI cost reporting (Runs page + Session Merchants page) ────────────────

/** Longest window either AI-cost report will chart/tabulate. Independent of
 *  SESSION_RUN_REPORT_MAX_DAYS so this report family can evolve on its own. */
export const AUTO_COUPON_COST_REPORT_MAX_DAYS = 30;

/**
 * Per-UTC-day AI cost/usage totals aggregated over AutoCouponRun docs, with
 * TemplateCaptureRun.llmTotalCost coalesced in by correlationId using the
 * SAME rule as IAutoCouponRun.aiCost (AutoCouponRun's own llmTotalCost wins
 * when > 0; TemplateCaptureRun is the fallback only for older-vintage
 * capture-origin runs that predate that field). Every day in the window is
 * present, zero-filled; the last entry is UTC "today so far". Shared shape
 * for both the Runs report and the Session Merchants report's trend.
 */
export interface IAutoCouponCostDailyEntry {
  /** UTC calendar day, YYYY-MM-DD. */
  date: string;
  /** All runs created that day, regardless of whether they incurred AI cost. */
  totalRuns: number;
  /** Runs whose coalesced cost is > 0 — i.e. actually invoked the AI
   *  pipeline. Denominator for avgCostPerRun (deliberately NOT totalRuns —
   *  most runs never touch the AI pipeline and would otherwise drag the
   *  average toward a misleading near-zero figure). */
  runsWithCost: number;
  /** Sum of coalesced cost (USD) across all runs that day. */
  totalCost: number;
  /** Sum of coalesced call counts across all runs that day. */
  totalCalls: number;
  /** totalCost / runsWithCost — 0 when runsWithCost is 0. */
  avgCostPerRun: number;
}

/** Grand totals across an entire report window (sum of its `daily` array). */
export interface IAutoCouponCostReportTotals {
  totalRuns: number;
  runsWithCost: number;
  totalCost: number;
  totalCalls: number;
  avgCostPerRun: number;
}

/**
 * Server-computed "worth a look" signal for a domain's AI spend — kept to a
 * small, explainable set of named reasons rather than a black-box score.
 * Checked in priority order (repeated_no_success first, the more specific,
 * actionable case) by AutoCouponsService.getCostReport(). Thresholds are
 * reasonable starting defaults, not derived from real spend data — expect to
 * retune after watching this run against real domains for a while.
 *  - "repeated_no_success": runsWithCost >= 3 && successfulCoupons === 0
 *  - "high_unevaluated_spend": totalCost >= 0.50 && unevaluatedCostShare >= 0.5
 */
export type TAutoCouponCostFlag =
  | "repeated_no_success"
  | "high_unevaluated_spend"
  | null;

/** One row of the "where is the spend concentrated" domain breakdown —
 *  summed over the WHOLE requested window (not per-day). Feeds the Runs
 *  report's table and its `mode=domain` CSV export. */
export interface IAutoCouponCostByDomainEntry {
  domain: string;
  totalRuns: number;
  runsWithCost: number;
  totalCost: number;
  totalCalls: number;
  avgCostPerRun: number;
  /** Runs that finished the coupon-evaluation loop (evaluationCompleted=true). */
  evaluationCompletedRuns: number;
  /** Summed across this domain's runs in the window. */
  successfulCoupons: number;
  /** totalCost / successfulCoupons; null when there were 0 successes (avoids
   *  a divide-by-zero/Infinity reading as if it were a real number). */
  costPerSuccess: number | null;
  /** Cost attributable to runs where evaluationCompleted=false — spend that,
   *  by definition, could never have produced a result. */
  unevaluatedCost: number;
  /** unevaluatedCost / totalCost, 0 when totalCost is 0. */
  unevaluatedCostShare: number;
  flag: TAutoCouponCostFlag;
}

// No outcome/flag fields here, unlike IAutoCouponCostByDomainEntry — a flag
// is meant to point at something actionable ("look at this merchant"); origin
// is a pipeline category (extension/session/capture), not an actionable
// target, so there's nothing to "go fix" at that granularity.
export interface IAutoCouponCostByOriginEntry {
  origin: TEvaluationOrigin;
  totalRuns: number;
  runsWithCost: number;
  totalCost: number;
  totalCalls: number;
  avgCostPerRun: number;
}

export interface IAutoCouponRunCostReportResponse {
  /** Ascending by date; one entry per day in the window (zero-filled). */
  daily: IAutoCouponCostDailyEntry[];
  days: number;
  /** The most recent day in the window (UTC today), or null if empty. */
  latestDate: string | null;
  /** Pre-summed — read this directly rather than re-reducing `daily`. */
  totals: IAutoCouponCostReportTotals;
  /** Top domains by cost over the window, descending, capped at the
   *  request's `domainLimit` — a display list. */
  byDomain: IAutoCouponCostByDomainEntry[];
  /** EVERY domain with flag !== null, regardless of cost rank, so a
   *  low-dollar-but-concerning domain isn't hidden by the cap above. */
  flaggedDomains: IAutoCouponCostByDomainEntry[];
  /** Cost by origin (extension/session/capture) over the window. */
  byOrigin: IAutoCouponCostByOriginEntry[];
}

/** One row of the session-merchant AI-spend leaderboard. Merchant.aiSpend is
 *  a LIFETIME running total (no day granularity), so this is NOT
 *  time-windowed — matches the per-row figure already shown on the Session
 *  Merchants table (Merchant.aiSpend.totalCost). */
export interface IAutoCouponSessionMerchantSpendEntry {
  merchantId: string;
  name: string;
  /** A merchant can have multiple domains; all are included for display. */
  domains: string[];
  countryCode: string | null;
  totalCost: number;
  updatedAt: string | null;
  autoTemplateCreationStatus: TTemplateCreationStatus;
  skuTemplateCreationStatus: TTemplateCreationStatus;
  /** "high_spend_no_template": totalCost >= 0.50 && neither half is "created". */
  flag: "high_spend_no_template" | null;
}

export interface IAutoCouponSessionMerchantsCostReportResponse {
  /** sum(Merchant.aiSpend.totalCost) for merchants with sessionRunId != null.
   *  Lifetime, independent of `days`/`daily` below. */
  lifetimeTotalCost: number;
  /** Number of session merchants included in lifetimeTotalCost/leaderboard. */
  sessionMerchantCount: number;
  /** Subset of the above with aiSpend.totalCost > 0. */
  sessionMerchantsWithSpend: number;
  /** Day-bucketed AI cost trend for the window, scoped to AutoCouponRun docs
   *  whose domain belongs to a session merchant. */
  daily: IAutoCouponCostDailyEntry[];
  days: number;
  latestDate: string | null;
  totals: IAutoCouponCostReportTotals;
  /** Top session merchants by lifetime aiSpend.totalCost, descending, capped. */
  leaderboard: IAutoCouponSessionMerchantSpendEntry[];
  /** EVERY merchant with flag !== null, regardless of the leaderboard cap. */
  flaggedMerchants: IAutoCouponSessionMerchantSpendEntry[];
}
